import { getAuth } from 'firebase/auth';

// ── API client ────────────────────────────────────────────────
// A thin wrapper around fetch that:
//  1. Reads VITE_API_URL from the environment at call time
//  2. Attaches the current Firebase ID token as a Bearer header
//  3. Throws on non-2xx responses with a parsed error message

async function getIdToken(): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const base = (import.meta as { env: Record<string, string> }).env.VITE_API_URL ?? '';
  const token = await getIdToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let message = `${method} ${path} failed with ${res.status}`;
    try {
      const err = await res.json();
      if (err.message) message = err.message;
      else if (err.error) message = err.error;
    } catch { /* ignore parse failures */ }
    throw new ApiError(res.status, message);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) => {
    if (params) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      const str = qs.toString();
      if (str) path = `${path}?${str}`;
    }
    return request<T>('GET', path);
  },
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
